import { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapContainer, TileLayer, Marker, Polyline, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import NamusImage from '@/components/NamusImage';
import InvestigatorPanel from '@/components/InvestigatorPanel';
import {
  User, MapPin, Calendar, Ruler, Scale, Clock,
  CheckCircle, AlertCircle, XCircle, Minus, HelpCircle,
  AlertTriangle, ShieldAlert, Bookmark, Eye,
  Send, Download, FileText, ExternalLink, ChevronDown, ChevronUp,
  Activity, Navigation, Info, Mail
} from 'lucide-react';

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom map icons
const getMissingPersonIcon = () => L.divIcon({
  className: 'custom-marker',
  html: `<div style="width:20px;height:20px;border-radius:50%;background:#10b981;border:2px solid #064e3b;box-shadow:0 2px 4px rgba(0,0,0,0.3);"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const getUnidentifiedIcon = () => L.divIcon({
  className: 'custom-marker',
  html: `<div style="width:20px;height:20px;border-radius:50%;background:#8b5cf6;border:2px solid #4c1d95;box-shadow:0 2px 4px rgba(0,0,0,0.3);"></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

// Haversine distance calculation
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Get confidence tier
const getConfidenceTier = (score) => {
  if (score >= 55) return { label: 'Strong', action: 'Warrants Priority Investigation', color: 'emerald', bg: 'bg-emerald-600', text: 'text-white', subtleBg: 'bg-emerald-100', subtleText: 'text-emerald-700', border: 'border-emerald-300' };
  if (score >= 40) return { label: 'Moderate', action: 'Warrants Investigation', color: 'amber', bg: 'bg-amber-500', text: 'text-white', subtleBg: 'bg-amber-100', subtleText: 'text-amber-700', border: 'border-amber-300' };
  return { label: 'Preliminary', action: 'Requires Further Data', color: 'slate', bg: 'bg-slate-500', text: 'text-white', subtleBg: 'bg-slate-100', subtleText: 'text-slate-600', border: 'border-slate-300' };
};

// Get data strength
const getDataStrength = (match) => {
  let score = 0;
  if (match.missing_person_age) score++;
  if (match.unidentified_person_age) score++;
  if (match.missing_person_photo) score++;
  if (match.unidentified_person_photo) score++;
  if (match.missing_person_city) score++;
  if (match.unidentified_person_city) score++;
  
  if (score >= 5) return { label: 'High', color: 'emerald' };
  if (score >= 3) return { label: 'Medium', color: 'amber' };
  return { label: 'Limited', color: 'slate' };
};

// Forensic significance weights for comparison attributes
// Higher = more forensically distinctive/exclusionary
const FORENSIC_WEIGHT = {
  tattoos: { weight: 5, label: 'Critical' },
  scars_piercings: { weight: 4, label: 'High' },
  distinguishing: { weight: 4, label: 'High' },
  clothing: { weight: 3, label: 'Moderate' },
  sex: { weight: 3, label: 'Moderate' },
  age: { weight: 2, label: 'Standard' },
  height: { weight: 2, label: 'Standard' },
  weight: { weight: 1, label: 'Low' },
  race: { weight: 2, label: 'Standard' },
  hair: { weight: 1, label: 'Low' },
  eye: { weight: 1, label: 'Low' },
};

// Relative time helper
const getRelativeTime = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
};

// Comparison result helper
const getComparisonResult = (val1, val2, field) => {
  if (!val1 || !val2 || val1 === 'Unknown' || val2 === 'Unknown') {
    return { type: 'unknown', label: 'Unknown', icon: Minus };
  }
  
  const v1 = String(val1).toLowerCase().trim();
  const v2 = String(val2).toLowerCase().trim();
  
  if (v1 === v2) return { type: 'match', label: 'Match', icon: CheckCircle };
  
  // Age comparison
  if (field === 'age') {
    const a1 = parseInt(val1);
    const a2 = parseInt(val2);
    if (!isNaN(a1) && !isNaN(a2)) {
      const diff = Math.abs(a1 - a2);
      if (diff <= 3) return { type: 'match', label: 'Match', icon: CheckCircle };
      if (diff <= 7) return { type: 'partial', label: 'Close', icon: AlertCircle };
      if (diff <= 15) return { type: 'partial', label: 'Possible', icon: AlertCircle };
    }
  }
  
  // Height comparison
  if (field === 'height') {
    const parseHeight = (h) => {
      const str = String(h).toLowerCase();
      const match = str.match(/(\d+)['\s]*(?:ft)?['\s]*(\d+)?/);
      if (match) return parseInt(match[1]) * 12 + (parseInt(match[2]) || 0);
      return null;
    };
    const h1 = parseHeight(val1);
    const h2 = parseHeight(val2);
    if (h1 && h2) {
      const diff = Math.abs(h1 - h2);
      if (diff <= 2) return { type: 'match', label: 'Match', icon: CheckCircle };
      if (diff <= 4) return { type: 'partial', label: 'Close', icon: AlertCircle };
    }
  }
  
  // Weight comparison
  if (field === 'weight') {
    const w1 = parseInt(String(val1).match(/(\d+)/)?.[1]);
    const w2 = parseInt(String(val2).match(/(\d+)/)?.[1]);
    if (w1 && w2) {
      const diff = Math.abs(w1 - w2);
      if (diff <= 5) return { type: 'match', label: 'Match', icon: CheckCircle };
      if (diff <= 15) return { type: 'partial', label: 'Close', icon: AlertCircle };
    }
  }
  
  // Race - partial match (check component overlap)
  if (field === 'race') {
    const split = (s) => s.split(/[\/,]+/).map(p => p.trim().toLowerCase()).filter(Boolean);
    const parts1 = split(v1);
    const parts2 = split(v2);
    const overlap = parts1.some(p1 => parts2.some(p2 => p1 === p2 || p1.includes(p2) || p2.includes(p1)));
    if (overlap) return { type: 'partial', label: 'Partial', icon: AlertCircle };
  }
  
  // Tattoo comparison
  if (field === 'tattoos') {
    const mpTattoo = String(val1 || '').toLowerCase();
    const upTattoo = String(val2 || '').toLowerCase();
    
    // Check for explicit "no tattoos" in UID
    const noTattooIndicators = ['no tattoo', 'none known', 'no visible', 'tattoos: none', 'unknown if'];
    const upHasNoTattoos = noTattooIndicators.some(phrase => upTattoo.includes(phrase));
    const mpHasTattoos = mpTattoo.length > 0 && !noTattooIndicators.some(phrase => mpTattoo.includes(phrase)) &&
      (mpTattoo.includes('tattoo') || mpTattoo.includes('tatt') || mpTattoo.includes('ink'));
    
    // HIGH CONFLICT: MP has distinctive tattoos, UID explicitly has none
    if (mpHasTattoos && upHasNoTattoos) {
      return { type: 'conflict', label: 'High Conflict', icon: XCircle };
    }
    
    // No data case
    if (!val1 && !val2) {
      return { type: 'unknown', label: 'No Data', icon: HelpCircle };
    }
    
    // MP has tattoos but UID data unknown
    if (mpHasTattoos && !upTattoo) {
      return { type: 'unknown', label: 'Unknown', icon: HelpCircle };
    }
    
    // Check for matching tattoo keywords
    const tattooKeywords = ['cross', 'skull', 'rose', 'dragon', 'heart', 'star', 'angel', 'tribal',
      'name', 'mama', 'mom', 'dad', 'rip', 'portrait', 'butterfly', 'eagle', 'wolf', 'snake'];
    
    const mpKeywords = tattooKeywords.filter(kw => mpTattoo.includes(kw));
    const upKeywords = tattooKeywords.filter(kw => upTattoo.includes(kw));
    const matchingKeywords = mpKeywords.filter(kw => upKeywords.includes(kw));
    
    if (matchingKeywords.length > 0) {
      return { type: 'match', label: 'Strong Match', icon: CheckCircle };
    }
    
    // Both have tattoos but different content
    if (mpHasTattoos && upTattoo && upTattoo.includes('tattoo')) {
      return { type: 'partial', label: 'Has Tattoos', icon: AlertCircle };
    }
    
    return { type: 'unknown', label: 'Unknown', icon: HelpCircle };
  }
  
  // Clothing/Brand comparison (includes watches, jewelry, eyewear)
  if (field === 'clothing') {
    const mpClothing = String(val1 || '').toLowerCase();
    const upClothing = String(val2 || '').toLowerCase();
    
    // No data case
    if (!val1 && !val2) {
      return { type: 'unknown', label: 'No Data', icon: HelpCircle };
    }
    if (!val1 || !val2) {
      return { type: 'unknown', label: 'Unknown', icon: HelpCircle };
    }
    
    // Comprehensive brand list including watches and jewelry
    const clothingBrands = {
      // Footwear
      'nike': ['nike', 'air jordan', 'jordan', 'air max', 'air force'],
      'adidas': ['adidas', 'yeezy'],
      'puma': ['puma'],
      'converse': ['converse', 'chuck taylor', 'chucks'],
      'vans': ['vans'],
      'timberland': ['timberland', 'timbs'],
      'dr_martens': ['dr martens', 'doc martens', 'docs'],
      'reebok': ['reebok'],
      'new_balance': ['new balance'],
      // Watches - Luxury
      'rolex': ['rolex', 'submariner', 'datejust', 'daytona'],
      'omega': ['omega', 'seamaster', 'speedmaster'],
      'cartier_watch': ['cartier watch', 'tank watch', 'santos'],
      'tag_heuer': ['tag heuer'],
      'breitling': ['breitling'],
      // Watches - Mid/Affordable
      'seiko': ['seiko'],
      'citizen': ['citizen', 'eco-drive'],
      'casio': ['casio', 'g-shock', 'gshock'],
      'timex': ['timex', 'ironman'],
      'fossil': ['fossil'],
      'fitbit': ['fitbit'],
      'apple_watch': ['apple watch'],
      'garmin': ['garmin'],
      // Jewelry
      'tiffany': ['tiffany', "tiffany's"],
      'pandora': ['pandora'],
      'david_yurman': ['david yurman', 'yurman'],
      'kay': ['kay jewelers'],
      'zales': ['zales'],
      'jostens': ['jostens', 'class ring'],
      // Eyewear
      'ray_ban': ['ray-ban', 'ray ban', 'rayban'],
      'oakley': ['oakley'],
      // Clothing
      'levis': ["levi's", 'levis'],
      'wrangler': ['wrangler'],
      'carhartt': ['carhartt'],
      'north_face': ['north face'],
    };
    
    // High priority categories - conflicts here = EXCLUDE
    const highPriorityKeywords = {
      footwear: ['shoe', 'shoes', 'sneaker', 'sneakers', 'boot', 'boots', 'sandal', 'sandals'],
      watch: ['watch', 'watches', 'wristwatch', 'timepiece', 'smartwatch', 'fitbit', 'g-shock'],
      jewelry: ['ring', 'rings', 'necklace', 'bracelet', 'earring', 'earrings', 'pendant', 'chain', 'class ring', 'wedding ring', 'wedding band'],
      eyewear: ['glasses', 'sunglasses', 'eyeglasses', 'shades'],
    };
    
    // Extract brands for each
    const getBrands = (text) => {
      const found = new Set();
      Object.entries(clothingBrands).forEach(([brand, variations]) => {
        if (variations.some(v => text.includes(v))) {
          found.add(brand);
        }
      });
      return found;
    };
    
    // Check which high-priority categories are present
    const getHighPriorityCategories = (text) => {
      const found = new Set();
      Object.entries(highPriorityKeywords).forEach(([category, keywords]) => {
        if (keywords.some(kw => text.includes(kw))) {
          found.add(category);
        }
      });
      return found;
    };
    
    const mpBrands = getBrands(mpClothing);
    const upBrands = getBrands(upClothing);
    const mpCategories = getHighPriorityCategories(mpClothing);
    const upCategories = getHighPriorityCategories(upClothing);
    
    // Check for matching brands
    const matchingBrands = [...mpBrands].filter(b => upBrands.has(b));
    if (matchingBrands.length > 0) {
      return { type: 'match', label: 'Brand Match', icon: CheckCircle };
    }
    
    // Check for high-priority category conflicts (footwear, watch, jewelry, eyewear)
    const sharedCategories = [...mpCategories].filter(c => upCategories.has(c));
    
    if (mpBrands.size > 0 && upBrands.size > 0 && sharedCategories.length > 0) {
      // Both have brands in same high-priority category but different = CONFLICT
      const categoryName = sharedCategories[0].charAt(0).toUpperCase() + sharedCategories[0].slice(1);
      return { type: 'conflict', label: `${categoryName} Conflict`, icon: XCircle };
    }
    
    // Both have clothing data but no brand match/conflict
    return { type: 'partial', label: 'Different', icon: AlertCircle };
  }
  
  // Scars/Piercings comparison
  if (field === 'scars_piercings') {
    const mpData = String(val1 || '').toLowerCase();
    const upData = String(val2 || '').toLowerCase();
    
    // No data case
    if (!val1 && !val2) {
      return { type: 'unknown', label: 'No Data', icon: HelpCircle };
    }
    if (!val1 || !val2) {
      return { type: 'unknown', label: 'Unknown', icon: HelpCircle };
    }
    
    // Check for explicit "no scars" or "no piercings"
    const noScarPhrases = ['no scar', 'no scars', 'no visible scar', 'scars: none'];
    const noPiercingPhrases = ['no piercing', 'no piercings', 'not pierced'];
    
    const mpHasNoScars = noScarPhrases.some(p => mpData.includes(p));
    const upHasNoScars = noScarPhrases.some(p => upData.includes(p));
    const mpHasNoPiercings = noPiercingPhrases.some(p => mpData.includes(p));
    const upHasNoPiercings = noPiercingPhrases.some(p => upData.includes(p));
    
    // Scar indicators
    const scarKeywords = ['scar', 'burn', 'birthmark', 'mole', 'mark', 'keloid'];
    const piercingKeywords = ['pierc', 'earring', 'gauge', 'stretched'];
    
    const mpHasScars = scarKeywords.some(k => mpData.includes(k)) && !mpHasNoScars;
    const upHasScars = scarKeywords.some(k => upData.includes(k)) && !upHasNoScars;
    const mpHasPiercings = piercingKeywords.some(k => mpData.includes(k)) && !mpHasNoPiercings;
    const upHasPiercings = piercingKeywords.some(k => upData.includes(k)) && !upHasNoPiercings;
    
    // Distinctive locations for scars
    const faceLocations = ['face', 'forehead', 'cheek', 'chin', 'lip', 'nose', 'eyebrow', 'neck'];
    const mpHasFaceScar = mpHasScars && faceLocations.some(loc => mpData.includes(loc));
    
    // Distinctive piercings (non-ear)
    const distinctivePiercings = ['nose', 'lip', 'tongue', 'eyebrow', 'navel', 'septum'];
    const mpHasDistinctivePiercing = mpHasPiercings && distinctivePiercings.some(p => mpData.includes(p));
    
    // CONFLICT: MP has distinctive facial scar, UID explicitly has no scars
    if (mpHasFaceScar && upHasNoScars) {
      return { type: 'conflict', label: 'Scar Conflict', icon: XCircle };
    }
    
    // CONFLICT: MP has distinctive piercing, UID explicitly has no piercings
    if (mpHasDistinctivePiercing && upHasNoPiercings) {
      return { type: 'conflict', label: 'Piercing Conflict', icon: XCircle };
    }
    
    // Check for matching locations
    const bodyLocations = ['face', 'arm', 'leg', 'chest', 'back', 'abdomen', 'hand', 'foot', 'neck'];
    const mpLocs = bodyLocations.filter(loc => mpData.includes(loc));
    const upLocs = bodyLocations.filter(loc => upData.includes(loc));
    const matchingLocs = mpLocs.filter(loc => upLocs.includes(loc));
    
    if (matchingLocs.length > 0) {
      return { type: 'match', label: 'Location Match', icon: CheckCircle };
    }
    
    // Both have scars/piercings but different locations
    if ((mpHasScars && upHasScars) || (mpHasPiercings && upHasPiercings)) {
      return { type: 'partial', label: 'Different Locations', icon: AlertCircle };
    }
    
    return { type: 'unknown', label: 'Unknown', icon: HelpCircle };
  }
  
  // Distinguishing Characteristics comparison (missing body parts, medical devices, etc.)
  if (field === 'distinguishing') {
    const mpData = String(val1 || '').toLowerCase();
    const upData = String(val2 || '').toLowerCase();
    
    if (!val1 && !val2) {
      return { type: 'unknown', label: 'No Data', icon: HelpCircle };
    }
    if (!val1 || !val2) {
      return { type: 'unknown', label: 'Unknown', icon: HelpCircle };
    }
    
    // Keywords for missing body parts
    const missingPartKeywords = [
      'missing', 'amputat', 'prosthetic', 'artificial', 'no finger', 'no toe', 'no hand', 'no foot',
      'no arm', 'no leg', 'lost finger', 'lost toe', 'lost hand', 'lost foot'
    ];
    
    // Keywords for dental features
    const dentalKeywords = ['teeth', 'tooth', 'dental', 'denture', 'gold tooth', 'gold teeth', 'edentulous'];
    
    // Keywords for medical devices
    const medicalKeywords = ['pacemaker', 'implant', 'plate', 'pin', 'rod', 'screw', 'replacement', 'prosthetic'];
    
    const mpHasMissing = missingPartKeywords.some(k => mpData.includes(k));
    const upHasMissing = missingPartKeywords.some(k => upData.includes(k));
    const mpHasDental = dentalKeywords.some(k => mpData.includes(k));
    const upHasDental = dentalKeywords.some(k => upData.includes(k));
    const mpHasMedical = medicalKeywords.some(k => mpData.includes(k));
    const upHasMedical = medicalKeywords.some(k => upData.includes(k));
    
    // Check for explicit "none noted"
    const upHasNone = upData.includes('none noted') || upData.includes('no distinguishing') || upData.includes('none known');
    
    // CONFLICT: MP is missing a body part but UID has "none noted"
    if (mpHasMissing && upHasNone) {
      return { type: 'conflict', label: 'Missing Part Conflict', icon: XCircle };
    }
    
    // MATCH: Both have similar characteristics
    if ((mpHasMissing && upHasMissing) || (mpHasMedical && upHasMedical)) {
      return { type: 'match', label: 'Characteristic Match', icon: CheckCircle };
    }
    
    if (mpHasDental && upHasDental) {
      return { type: 'match', label: 'Dental Match', icon: CheckCircle };
    }
    
    // Has data but no match/conflict
    if (mpHasMissing || mpHasDental || mpHasMedical) {
      return { type: 'partial', label: 'MP Has Data', icon: AlertCircle };
    }
    
    return { type: 'unknown', label: 'Unknown', icon: HelpCircle };
  }
  
  return { type: 'conflict', label: 'Differs', icon: XCircle };
};

export default function InvestigativeMatchCard({ match, index, leVerified = false, isAdmin = false }) {
  const [expanded, setExpanded] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const navigate = useNavigate();
  
  // Handle both formats: flat (from simple-matches) and nested (from sample-matches)
  const mpData = match.missing_person || {};
  const upData = match.unidentified_person || {};
  
  const mp = {
    id: match.missing_person_id || mpData.case_id,
    name: match.missing_person_name || mpData.name || 'Unknown',
    photo: match.missing_person_photo || mpData.photo_url || mpData.photo_base64 || mpData.primary_photo_base64,
    age: match.missing_person_age || mpData.age,
    sex: match.missing_person_sex || mpData.gender,
    race: match.missing_person_race || mpData.race,
    height: match.missing_person_height || mpData.height,
    weight: match.missing_person_weight || mpData.weight,
    hair: match.missing_person_hair_color || mpData.hair_color,
    eye: match.missing_person_eye_color || mpData.eye_color,
    tattoos: match.missing_person_tattoos || mpData.tattoos || mpData.scars_marks_tattoos,
    scars_piercings: match.missing_person_scars_piercings || mpData.scars || mpData.piercings || mpData.scars_marks_tattoos,
    distinguishing: match.missing_person_distinguishing || mpData.distinguishing_features || mpData.physical_features || mpData.medical_conditions,
    clothing: match.missing_person_clothing || mpData.clothing || mpData.last_seen_wearing || mpData.clothing_description,
    city: match.missing_person_city || mpData.city,
    state: match.missing_person_state || mpData.state || mpData.location,
    lat: match.missing_person_lat || mpData.latitude,
    lng: match.missing_person_lng || mpData.longitude,
    lastSeen: match.missing_person_last_seen_date || mpData.last_seen_date,
  };
  
  const up = {
    id: match.unidentified_person_id || upData.case_id,
    name: match.unidentified_person_name || upData.name || 'Unidentified',
    photo: match.unidentified_person_photo || upData.photo_url || upData.photo_base64 || upData.primary_photo_base64,
    age: match.unidentified_person_age || upData.age,
    sex: match.unidentified_person_sex || upData.gender,
    race: match.unidentified_person_race || upData.race,
    height: match.unidentified_person_height || upData.height,
    weight: match.unidentified_person_weight || upData.weight,
    hair: match.unidentified_person_hair_color || upData.hair_color,
    eye: match.unidentified_person_eye_color || upData.eye_color,
    tattoos: match.unidentified_person_tattoos || upData.tattoos || upData.scars_marks_tattoos,
    scars_piercings: match.unidentified_person_scars_piercings || upData.scars || upData.piercings || upData.scars_marks_tattoos,
    distinguishing: match.unidentified_person_distinguishing || upData.distinguishing_features || upData.physical_features || upData.medical_conditions,
    clothing: match.unidentified_person_clothing || upData.clothing || upData.clothing_found || upData.clothing_description,
    city: match.unidentified_person_city || upData.city,
    state: match.unidentified_person_state || upData.state || upData.location,
    lat: match.unidentified_person_lat || upData.latitude,
    lng: match.unidentified_person_lng || upData.longitude,
    dateFound: match.unidentified_person_date_found || upData.date_found,
  };
  
  const score = Math.round(match.match_score || 0);
  const confidence = getConfidenceTier(score);
  const dataStrength = getDataStrength(match);
  
  // Calculate distance
  const distance = useMemo(() => {
    if (mp.lat && mp.lng && up.lat && up.lng) {
      return Math.round(calculateDistance(mp.lat, mp.lng, up.lat, up.lng));
    }
    return null;
  }, [mp.lat, mp.lng, up.lat, up.lng]);
  
  // Build comparison rows
  const comparisonRows = [
    { attr: 'Sex', mp: mp.sex, up: up.sex, field: 'sex' },
    { attr: 'Age', mp: mp.age ? `${mp.age} yrs` : null, up: up.age ? `${up.age} yrs` : null, field: 'age' },
    { attr: 'Race', mp: mp.race, up: up.race, field: 'race' },
    { attr: 'Height', mp: mp.height, up: up.height, field: 'height' },
    { attr: 'Weight', mp: mp.weight, up: up.weight, field: 'weight' },
    { attr: 'Hair', mp: mp.hair, up: up.hair, field: 'hair' },
    { attr: 'Eye Color', mp: mp.eye, up: up.eye, field: 'eye' },
    { attr: 'Tattoos', mp: mp.tattoos, up: up.tattoos, field: 'tattoos' },
    { attr: 'Scars/Piercings', mp: mp.scars_piercings, up: up.scars_piercings, field: 'scars_piercings' },
    { attr: 'Distinguishing', mp: mp.distinguishing, up: up.distinguishing, field: 'distinguishing' },
    { attr: 'Clothing', mp: mp.clothing, up: up.clothing, field: 'clothing' },
  ];
  
  // Get conflicts including tattoo-specific warnings
  const conflicts = useMemo(() => {
    const issues = [];
    let hasTattooConflict = false;
    
    comparisonRows.forEach(row => {
      const result = getComparisonResult(row.mp, row.up, row.field);
      if (result.type === 'conflict') {
        if (row.field === 'tattoos' && result.label === 'High Conflict') {
          hasTattooConflict = true;
          issues.unshift(`⚠️ High Conflict: Missing person has distinctive tattoos, UID reports none`);
        } else {
          issues.push(`${row.attr}: ${row.mp || 'Unknown'} vs ${row.up || 'Unknown'}`);
        }
      }
    });
    if (distance && distance > 500) {
      issues.push(`Significant distance: ${distance} miles apart`);
    }
    // Tattoo *content* warning chips from the V2 matching pipeline
    // (e.g. Hughes "Loyalty" vs UP11513 "FROY"). The chip text already
    // includes the comparison + score impact and is safe to surface
    // verbatim. Show before generic conflicts since it's highly specific.
    const md = match?.match_details || {};
    const warningChips = Array.isArray(md.warning_chips) ? md.warning_chips : [];
    warningChips.forEach((chip) => {
      if (typeof chip === 'string' && chip.trim()) {
        issues.unshift(chip);
      }
    });
    return issues;
  }, [comparisonRows, distance, match]);
  
  // Map data
  const hasMapData = mp.lat && mp.lng && up.lat && up.lng;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className="border border-slate-200 bg-white rounded-lg shadow-sm overflow-hidden"
      data-testid={`investigative-match-card-${index}`}
    >
      {/* ========== MATCH SUMMARY BAR ========== */}
      <div className={`px-4 py-3 border-b flex flex-wrap items-center justify-between gap-3 ${
        confidence.color === 'emerald' ? 'bg-emerald-50 border-emerald-200' :
        confidence.color === 'amber' ? 'bg-amber-50 border-amber-200' :
        'bg-slate-50 border-slate-200'
      }`}>
        <div className="flex items-center gap-3">
          {/* Confidence tier as headline */}
          <div className="flex items-baseline gap-2.5" data-testid="confidence-headline">
            <span className={`text-base font-bold uppercase tracking-wide ${
              confidence.color === 'emerald' ? 'text-emerald-800' :
              confidence.color === 'amber' ? 'text-amber-800' :
              'text-slate-700'
            }`}>
              {confidence.label}
            </span>
            <span className={`text-xs font-medium ${
              confidence.color === 'emerald' ? 'text-emerald-600' :
              confidence.color === 'amber' ? 'text-amber-600' :
              'text-slate-500'
            }`}>
              {confidence.action}
            </span>
          </div>
          
          {/* Raw score - secondary */}
          <Badge className={`${confidence.subtleBg} ${confidence.subtleText} ${confidence.border} text-[10px] font-mono`} data-testid="match-score-badge">
            {score}%
          </Badge>
          
          {/* Data Strength */}
          <Badge className={`text-[10px] font-mono ${
            dataStrength.color === 'emerald' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
            dataStrength.color === 'amber' ? 'bg-amber-100 text-amber-700 border-amber-300' :
            'bg-slate-100 text-slate-600 border-slate-300'
          }`}>
            {dataStrength.label} Data
          </Badge>
        </div>
        
        {/* Right side: Warnings + Last updated */}
        <div className="flex items-center gap-3">
          {conflicts.length > 0 && (
            <span className="flex items-center gap-1 text-xs text-rose-600">
              <ShieldAlert className="w-3.5 h-3.5" />
              {conflicts.length} warning{conflicts.length > 1 ? 's' : ''}
            </span>
          )}
          {distance && (
            <span className="text-xs text-slate-500 font-mono">
              <MapPin className="w-3 h-3 inline mr-1" />{distance} mi
            </span>
          )}
          {match.updated_at && (
            <span className="text-[10px] text-slate-400 font-mono" data-testid="last-updated" title={`Scored: ${new Date(match.updated_at).toLocaleDateString()} ${new Date(match.updated_at).toLocaleTimeString()}`}>
              <Clock className="w-3 h-3 inline mr-0.5" />
              {getRelativeTime(match.updated_at)}
            </span>
          )}
        </div>
      </div>
      
      {/* ========== VERIFICATION NOTICE (persistent, integrated) ========== */}
      <div className="px-4 py-1.5 bg-slate-100 border-b border-slate-200 flex items-center gap-2" data-testid="verification-notice">
        <ShieldAlert className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        <p className="text-[11px] text-slate-500 leading-snug">
          This is an <span className="font-semibold text-slate-600">algorithmic signal</span>, not a confirmed identification. Professional verification by law enforcement or a medical examiner is required before any action is taken.
        </p>
      </div>
      
      {/* ========== CONVICTION / REMAINS LOST BANNER ========== */}
      {(match.mp_flags || []).some(f => 
        f === 'CONVICTED_HOMICIDE_REMAINS_LOST_AT_SEA' || f === 'CONVICTION_CLOSED' || 
        f === 'REMAINS_LOST_AT_SEA' || f === 'CONVICTED_HOMICIDE'
      ) && (
        <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-200 flex items-start gap-2.5" data-testid="conviction-banner">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 leading-relaxed">
            <span className="font-semibold">Note:</span> This individual's case has a closed conviction. Remains were not recovered. Matches shown are for research purposes only and are unlikely to represent this individual.
          </p>
        </div>
      )}
      
      {/* ========== SIDE-BY-SIDE CASE CARDS ========== */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr,1fr] gap-px bg-slate-200">
        {/* Missing Person */}
        <div className="bg-white p-4">
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px] font-mono">
              MISSING PERSON
            </Badge>
          </div>
          <div className="flex gap-4">
            {/* Photo */}
            <div className="w-20 h-24 flex-shrink-0 bg-slate-100 border border-slate-200 rounded overflow-hidden">
              {mp.photo ? (
                <NamusImage src={mp.photo} alt={mp.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="w-8 h-8 text-slate-400" />
                </div>
              )}
            </div>
            {/* Info */}
            <div className="flex-1 min-w-0">
              <Link to={`/case/${mp.id}`} className="font-semibold text-slate-800 hover:text-emerald-600 transition-colors text-sm line-clamp-2">
                {mp.name}
              </Link>
              <div className="mt-2 space-y-1 text-xs text-slate-500">
                {mp.age && <div><User className="w-3 h-3 inline mr-1" />{mp.age} years old</div>}
                {mp.height && <div><Ruler className="w-3 h-3 inline mr-1" />{mp.height}</div>}
                {(mp.city || mp.state) && (
                  <div><MapPin className="w-3 h-3 inline mr-1" />{[mp.city, mp.state].filter(Boolean).join(', ')}</div>
                )}
                {mp.lastSeen && (
                  <div><Calendar className="w-3 h-3 inline mr-1" />Last seen: {new Date(mp.lastSeen).toLocaleDateString()}</div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Unidentified Person */}
        <div className="bg-white p-4">
          <div className="flex items-center gap-2 mb-3">
            <Badge className="bg-violet-100 text-violet-700 border-violet-300 text-[10px] font-mono">
              UNIDENTIFIED
            </Badge>
          </div>
          <div className="flex gap-4">
            {/* Photo */}
            <div className="w-20 h-24 flex-shrink-0 bg-slate-100 border border-slate-200 rounded overflow-hidden">
              {up.photo ? (
                <NamusImage src={up.photo} alt={up.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User className="w-8 h-8 text-slate-400" />
                </div>
              )}
            </div>
            {/* Info */}
            <div className="flex-1 min-w-0">
              <Link to={`/case/${up.id}`} className="font-semibold text-slate-800 hover:text-violet-600 transition-colors text-sm line-clamp-2">
                {up.name}
              </Link>
              <div className="mt-2 space-y-1 text-xs text-slate-500">
                {up.age && <div><User className="w-3 h-3 inline mr-1" />{up.age} years old</div>}
                {up.height && <div><Ruler className="w-3 h-3 inline mr-1" />{up.height}</div>}
                {(up.city || up.state) && (
                  <div><MapPin className="w-3 h-3 inline mr-1" />{[up.city, up.state].filter(Boolean).join(', ')}</div>
                )}
                {up.dateFound && (
                  <div><Calendar className="w-3 h-3 inline mr-1" />Found: {new Date(up.dateFound).toLocaleDateString()}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* ========== COMPARISON TABLE ========== */}
      <div className="border-t border-slate-200">
        <div className="px-4 py-2 bg-slate-50 flex items-center justify-between">
          <span className="text-xs text-slate-500 uppercase tracking-wider font-medium">Attribute Comparison</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-slate-500 hover:text-slate-700 gap-1 h-7"
          >
            {expanded ? 'Collapse' : 'Expand'}
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </Button>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-200 text-slate-500 uppercase tracking-wider bg-slate-50">
                <th className="text-left py-2 px-4 font-medium w-[100px]">Attribute</th>
                <th className="text-left py-2 px-4 font-medium">Missing</th>
                <th className="text-left py-2 px-4 font-medium">Unidentified</th>
                <th className="text-center py-2 px-4 font-medium w-[90px]">Result</th>
                <th className="text-center py-2 px-2 font-medium w-[50px]" title="Forensic significance">Wt</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.slice(0, expanded ? undefined : 4).map((row, idx) => {
                const result = getComparisonResult(row.mp, row.up, row.field);
                const Icon = result.icon;
                const fw = FORENSIC_WEIGHT[row.field] || { weight: 1, label: 'Low' };
                
                // Truncate long text (especially for tattoos)
                const truncate = (text, max = 40) => {
                  if (!text) return '—';
                  const str = String(text);
                  return str.length > max ? str.substring(0, max) + '...' : str;
                };
                
                return (
                  <tr key={idx} className={`border-b border-slate-100 ${row.field === 'tattoos' && result.type === 'conflict' ? 'bg-rose-50' : ''}`}>
                    <td className="py-2 px-4 text-slate-500">{row.attr}</td>
                    <td className="py-2 px-4 text-slate-700 max-w-[150px]" title={row.mp || ''}>
                      {truncate(row.mp)}
                    </td>
                    <td className="py-2 px-4 text-slate-700 max-w-[150px]" title={row.up || ''}>
                      {truncate(row.up)}
                    </td>
                    <td className="py-2 px-4 text-center">
                      <Badge className={`text-[10px] gap-1 ${
                        result.type === 'match' ? 'bg-emerald-100 text-emerald-700 border-emerald-300' :
                        result.type === 'partial' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                        result.type === 'conflict' ? 'bg-rose-100 text-rose-700 border-rose-300' :
                        'bg-slate-100 text-slate-500 border-slate-300'
                      }`}>
                        <Icon className="w-3 h-3" />
                        {result.label}
                      </Badge>
                    </td>
                    <td className="py-2 px-2 text-center" title={`${fw.label} forensic significance`}>
                      <div className="flex items-center justify-center gap-0.5">
                        {[...Array(5)].map((_, i) => (
                          <div
                            key={i}
                            className={`w-1 rounded-full ${
                              i < fw.weight
                                ? fw.weight >= 4 ? 'bg-rose-400 h-2.5' : fw.weight >= 3 ? 'bg-amber-400 h-2' : 'bg-slate-300 h-1.5'
                                : 'bg-slate-100 h-1'
                            }`}
                          />
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* ========== INVESTIGATOR PANEL (always visible if data exists) ========== */}
      <div className="px-4 py-1">
        <InvestigatorPanel match={match} warningCount={conflicts.length} leVerified={leVerified} />
      </div>
      
      {/* ========== EXPANDED SECTIONS ========== */}
      {expanded && (
        <>
          {/* Biological Plausibility */}
          {mp.lastSeen && up.dateFound && (
            <div className="border-t border-slate-200 p-4 bg-slate-50">
              <h4 className="text-xs text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5" /> Biological Plausibility
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                <div>
                  <div className="text-slate-500">Missing Date</div>
                  <div className="text-slate-700 font-mono">{new Date(mp.lastSeen).toLocaleDateString()}</div>
                </div>
                <div>
                  <div className="text-slate-500">Found Date</div>
                  <div className="text-slate-700 font-mono">{new Date(up.dateFound).toLocaleDateString()}</div>
                </div>
                <div>
                  <div className="text-slate-500">Gap</div>
                  <div className="text-slate-700 font-mono">
                    {Math.round((new Date(up.dateFound) - new Date(mp.lastSeen)) / (1000 * 60 * 60 * 24))} days
                  </div>
                </div>
                <div>
                  <div className="text-slate-500">Result</div>
                  <Badge className={`text-[10px] ${
                    new Date(up.dateFound) >= new Date(mp.lastSeen)
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-rose-100 text-rose-700'
                  }`}>
                    {new Date(up.dateFound) >= new Date(mp.lastSeen) ? 'Compatible' : 'Conflict'}
                  </Badge>
                </div>
              </div>
            </div>
          )}
          
          {/* Map Section */}
          {hasMapData && (
            <div className="border-t border-slate-200">
              <div className="px-4 py-2 bg-slate-50 flex items-center justify-between">
                <span className="text-xs text-slate-500 uppercase tracking-wider flex items-center gap-2">
                  <Navigation className="w-3.5 h-3.5" /> Geographic Analysis
                </span>
                {distance && (
                  <Badge className="text-[10px] bg-slate-200 text-slate-600">
                    {distance} miles apart
                  </Badge>
                )}
              </div>
              <div className="h-[200px]">
                <MapContainer
                  center={[(mp.lat + up.lat) / 2, (mp.lng + up.lng) / 2]}
                  zoom={distance > 200 ? 5 : distance > 50 ? 7 : 9}
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom={false}
                  dragging={true}
                  zoomControl={true}
                  attributionControl={false}
                >
                  <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png" />
                  <Marker position={[mp.lat, mp.lng]} icon={getMissingPersonIcon()}>
                    <Tooltip permanent direction="top" offset={[0, -10]} className="!bg-emerald-600 !text-white !border-0 !rounded !px-2 !py-0.5 !text-[9px]">
                      Last Seen
                    </Tooltip>
                  </Marker>
                  <Marker position={[up.lat, up.lng]} icon={getUnidentifiedIcon()}>
                    <Tooltip permanent direction="bottom" offset={[0, 10]} className="!bg-violet-600 !text-white !border-0 !rounded !px-2 !py-0.5 !text-[9px]">
                      Found
                    </Tooltip>
                  </Marker>
                  <Polyline positions={[[mp.lat, mp.lng], [up.lat, up.lng]]} color="#6366f1" weight={2} dashArray="6, 6" opacity={0.7} />
                </MapContainer>
              </div>
            </div>
          )}
          
          {/* Conflict Section */}
          {conflicts.length > 0 && (
            <div className="border-t border-rose-200 bg-rose-50 p-4">
              <h4 className="text-xs font-semibold text-rose-700 mb-2 flex items-center gap-2">
                <ShieldAlert className="w-3.5 h-3.5" /> Why this may not be a match
              </h4>
              <ul className="space-y-1">
                {conflicts.map((conflict, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-xs text-rose-600">
                    <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                    {conflict}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      
      {/* ========== ACTION PANEL ========== */}
      <div className="border-t border-slate-200 bg-slate-50">
        {/* Secondary actions row */}
        <div className="px-4 pt-3 pb-2 flex flex-wrap gap-2 items-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsSaved(!isSaved)}
            className="text-xs gap-1.5 h-8 border-slate-300 hover:bg-slate-100"
          >
            <Bookmark className={`w-3.5 h-3.5 ${isSaved ? 'fill-current text-amber-500' : ''}`} />
            {isSaved ? 'Saved' : 'Save'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsFollowing(!isFollowing)}
            className="text-xs gap-1.5 h-8 border-slate-300 hover:bg-slate-100"
          >
            <Eye className={`w-3.5 h-3.5 ${isFollowing ? 'text-emerald-600' : ''}`} />
            {isFollowing ? 'Following' : 'Follow'}
          </Button>
          <Link to={`/comparison/${match.id}`} className="ml-auto">
            <Button variant="outline" size="sm" className="text-xs gap-1.5 h-8 border-slate-300 hover:bg-slate-100">
              <ExternalLink className="w-3.5 h-3.5" />
              Full Analysis
            </Button>
          </Link>
          {/* Admin one-click outreach draft — third entry point into
              the case-pair-table template flow (after CaseDetail's
              "Draft for top match" banner and the create-modal button).
              Deep-links into /admin/outreach with autogen=1 so the
              templated letter is rendered to disk before the admin
              even sees the modal. */}
          {isAdmin && mp.id && up.id && (
            <Button
              variant="default"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '_');
                const aShort = String(mp.id).slice(0, 8);
                const bShort = String(up.id).slice(0, 8);
                const letterPath = `/app/backend/content/outreach_${aShort}_${bShort}_${stamp}.md`;
                const caseIds = `${mp.id} ${up.id}`;
                const qs = new URLSearchParams({
                  createOpen: '1',
                  template: 'case_pair_with_tables',
                  letter_path: letterPath,
                  case_ids: caseIds,
                  autogen: '1',
                });
                navigate(`/admin/outreach?${qs.toString()}`);
              }}
              className="text-xs gap-1.5 h-8 bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="match-row-draft-letter-btn"
              title="Draft an outreach letter for this pair (admin-only)">
              <Mail className="w-3.5 h-3.5" />
              Draft letter
            </Button>
          )}
        </div>
        {/* GEOCODING_ARTIFACT — muted data note below action row */}
        {[...(match.mp_flags || []), ...(match.uid_flags || []), ...(match.flags || [])].includes('GEOCODING_ARTIFACT') && (
          <div className="px-4 pb-1.5" data-testid="geocoding-artifact-note">
            <span className="text-[11px] text-slate-400 cursor-help border-b border-dotted border-slate-300" title="This UID's location is city-level only — geographic scoring may be less precise">&#9888; Coordinates may be approximate</span>
          </div>
        )}
        {/* Primary CTA - Submit Tip */}
        <div className="px-4 pb-3">
          <Link to={`/tip/${mp.id}`} className="block">
            <Button data-testid="submit-tip-cta" className="w-full gap-2 h-10 bg-blue-600 hover:bg-blue-500 text-white font-medium text-sm shadow-sm">
              <Send className="w-4 h-4" />
              Have information? Submit a tip about this connection
            </Button>
          </Link>
        </div>
      </div>
      
    </motion.div>
  );
}
